/*
 * cn-fireworks.js — Google-style championship fireworks Easter egg.
 *
 * Modeled on Google's sports SERP celebration: a full-viewport <canvas> overlay
 * with `pointer-events:none` (so it can NEVER touch the purchase path) that auto-
 * plays a burst of particles tinted to a passed-in color array, then removes
 * itself. Here it's themed to the US flag (red / white / blue).
 *
 * Triggers:
 *   1. Tapping the US flag icon  (.flag-item[data-value="🇺🇸"])
 *   2. Hitting Add to Cart with a US flag in the embroidery field
 *
 * Public API: window.cnFireworks(colors?, durationMs?)
 */
(function () {
  // Vivid, saturated US palette — bright enough to pop on any background.
  var US_COLORS = ['#FF2A4D', '#FFFFFF', '#2E6BFF']; // bright red / white / blue
  var running = false;

  // Pre-render one glow sprite per color (white-hot core -> colored halo ->
  // transparent), built once and cached. Drawing this with a single drawImage is
  // far cheaper than per-particle shadowBlur + arc fills, and additive blending
  // ('lighter') makes overlaps brighten just like a real burst.
  var glowCache = {};
  function glowSprite(color) {
    if (glowCache[color]) return glowCache[color];
    var s = 32, oc = document.createElement('canvas');
    oc.width = oc.height = s;
    var g = oc.getContext('2d');
    var grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.25, color);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
    glowCache[color] = oc;
    return oc;
  }

  function fireworks(colors, duration) {
    if (running) return;            // don't stack overlapping shows
    // Respect reduced-motion: skip the show entirely (also the lightest path).
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    running = true;
    colors = colors || US_COLORS;
    duration = duration || 3500;

    var c = document.createElement('canvas');
    c.style.cssText =
      'position:fixed;inset:0;width:100%;height:100%;pointer-events:none;z-index:2147483647;';
    document.body.appendChild(c);

    var ctx = c.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 1.5); // cap DPR — lighter fill cost
    function resize() {
      c.width = window.innerWidth * dpr;
      c.height = window.innerHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    window.addEventListener('resize', resize);

    var GRAV = 0.045;     // gravity pulling exploded sparks down
    var RISE_FRAMES = 70; // rocket rise time — slower rise reads as a bigger, higher shell
    var W = window.innerWidth, H = window.innerHeight;
    var particles = [];
    var rockets = [];
    function rnd(a, b) { return a + Math.random() * (b - a); }

    // A rocket shoots up from the bottom, slows under gravity, and bursts at apex.
    // Burst height varies per shell (some climb higher up the page), expressed as a
    // fraction of the viewport so a tall, slow shell can't sail off the top. From a
    // target apex + RISE_FRAMES we solve back for launch speed and per-rocket gravity,
    // so every shell reaches its apex in the same time — only the height changes.
    function launch() {
      var apex = rnd(0.45, 0.85) * H;   // burst point, 45–85% up the viewport
      var v = 2 * apex / RISE_FRAMES;   // initial speed to reach that apex in RISE_FRAMES
      rockets.push({
        x: rnd(W * 0.2, W * 0.8),
        y: H + 8,
        vx: rnd(-2.5, 2.5),             // horizontal velocity -> angled launch
        vy: -v,                         // upward; gravity curves it into an arc
        g: v / RISE_FRAMES,             // per-rocket gravity holds the rise time ~constant
        color: colors[Math.floor(Math.random() * colors.length)]
      });
    }

    // mvx = rocket's residual horizontal momentum at burst, nudged into the sparks
    // so the explosion drifts the way the shell was traveling (conservation-ish).
    function explode(x, y, color, mvx) {
      mvx = mvx || 0;
      var n = 46 + Math.floor(Math.random() * 28);   // fuller burst
      for (var i = 0; i < n; i++) {
        var ang = (Math.PI * 2 * i) / n;
        var spd = rnd(2.5, 6.5);                      // wider radius -> bigger blast
        particles.push({
          x: x, y: y,
          vx: Math.cos(ang) * spd + mvx * 0.3,
          vy: Math.sin(ang) * spd,
          life: 1, color: color, size: rnd(1.8, 3.6)
        });
      }
    }

    var start = null, lastLaunch = 0;

    function frame(t) {
      if (start === null) start = t;
      var elapsed = t - start;

      // Transparent canvas: clear each frame so the page underneath is never
      // tinted. Fade comes from each particle's own alpha, not a screen overlay.
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = 'lighter'; // additive — overlaps glow brighter

      if (t - lastLaunch > 380 && elapsed < duration) { launch(); lastLaunch = t; }

      // Rising rockets — glow head (sprite) plus a cheap colored streak tail.
      for (var r = rockets.length - 1; r >= 0; r--) {
        var rk = rockets[r];
        rk.x += rk.vx;
        rk.y += rk.vy;
        rk.vx *= 0.992;   // mild air drag -> the arc gently curves over
        rk.vy += rk.g;    // gravity bleeds vertical speed toward zero (apex)
        ctx.globalAlpha = 0.9;
        ctx.strokeStyle = rk.color;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(rk.x, rk.y);
        ctx.lineTo(rk.x - rk.vx, rk.y - rk.vy); // streak trails along the flight path
        ctx.stroke();
        ctx.drawImage(glowSprite(rk.color), rk.x - 6, rk.y - 6, 12, 12);
        if (rk.vy >= 0) { explode(rk.x, rk.y, rk.color, rk.vx); rockets.splice(r, 1); }
      }

      // Exploded sparks — one cached glow sprite per particle, single drawImage.
      for (var i = particles.length - 1; i >= 0; i--) {
        var p = particles[i];
        p.vy += GRAV; p.x += p.vx; p.y += p.vy; p.life -= 0.009; // slower fade -> trails linger
        if (p.life <= 0) { particles.splice(i, 1); continue; }
        ctx.globalAlpha = p.life;
        var d = p.size * 6;   // larger glow
        ctx.drawImage(glowSprite(p.color), p.x - d / 2, p.y - d / 2, d, d);
      }
      ctx.globalAlpha = 1;

      if (elapsed < duration || particles.length || rockets.length) {
        requestAnimationFrame(frame);
      } else {
        window.removeEventListener('resize', resize);
        c.remove();
        running = false;
      }
    }
    requestAnimationFrame(frame);
  }

  window.cnFireworks = fireworks;

  // --- Trigger helpers -------------------------------------------------------
  function fieldHasUSFlag() {
    var f = document.getElementById('embroidery-field');
    return !!f && (f.value || '').indexOf('🇺🇸') !== -1; // 🇺🇸
  }

  // --- Trigger 1: tap the US flag icon --------------------------------------
  document.addEventListener('click', function (e) {
    var item = e.target.closest && e.target.closest('.flag-item[data-value="🇺🇸"]');
    if (item) fireworks();
  });

  // --- Trigger 2: Add to Cart with a US flag in the embroidery field ---------
  // Both the PDP button and the pinned-bar clone funnel through the real
  // [data-add-to-cart] button (the clone calls realBtn.click()), so one
  // delegated listener covers every add path. Skip disabled (sold-out) clicks.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-add-to-cart]');
    if (!btn || btn.hasAttribute('aria-disabled')) return;
    if (fieldHasUSFlag()) fireworks();
  });
})();
